import { useSyncExternalStore } from 'react';
// Dados compartilhados no grupo (0.25): a obra, a viagem, a turma, o imóvel ou o consultório ficam no grupo e
// cada membro vê e altera conforme o papel. Cada ficha vai em partes: base, "fin" (valores) e "clin" (clínico);
// o servidor só entrega a parte que o papel pode ver (migracao-0.25.sql).
// A cópia de cada pessoa fica na própria aba dela (Reg.g = id do grupo), então avisos, busca, vencimentos e
// o Planner funcionam igual. A cada 30 segundos (e logo depois de uma alteração) o app envia e recebe.
import { AppState } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from './supabase';
import { DEMO } from './env';
import { getDoc, onDocChange, setDoc } from '../store/store';
import { definirGrupoDaNova } from '../store/modulos';
import { MOD_IDS, type ModId, type Reg, type Anexo } from '../store/types';
import { DEFS } from '../modulos/defs';
import { ehColaborativa, papeisDaAba, segDaChave, type Seg } from '../modulos/papeis';
import { alterarGrupo, gruposEmCache, meuId, recarregarGrupos, registrarDemo, type MeuGrupo } from './grupos';
import { cifrado, cofreAberto, decifrar } from './cofre';
import { enviarArquivoAoGrupo } from './arquivosGrupo';
import { temLocal } from './anexos';
import { avisarAgora, temPush } from './push';
import { ctxDe } from '../modulos/ctx';
import { norm } from './date';
import { t } from './i18n';
import { anexoSeguro } from './linkSeguro';
import { cicloEstudosGrupo } from './estudosGrupo';

const SEGS: Seg[] = ['base', 'fin', 'clin'];
const ehMod = (a: string): a is ModId => (MOD_IDS as string[]).includes(a);
const colDe = (mod: ModId, col: string) => DEFS[mod].colecoes.find(c => c.id === col);

// ─── Permissões (as mesmas regras do servidor, para a tela não oferecer o que vai ser recusado) ───
export type Acesso = { admin: boolean; segs: Set<Seg>; ver: (col: string) => boolean; editar: (col: string) => boolean; nome: string };
const TUDO: Acesso = { admin: true, segs: new Set(SEGS), ver: () => true, editar: () => true, nome: '' };

export const grupoPorId = (id: string | null | undefined) => (id ? gruposEmCache().lista?.find(g => g.id === id) : undefined);

// Texto 21 (A1): nos grupos de Leitura (livros, cursos, filmes e artigos), quem NÃO tem a aba só visualiza, mesmo que
// quem administra tenha liberado a edição para essa pessoa. Quem administra só libera, de fato, para quem também usa a aba.
const soVerNaAba = new Set<string>();
/** A tela da aba marca se a pessoa está nela como convidada (sem a aba). */
export function marcarSoVerNaAba(aba: string, on: boolean) { if (on) soVerNaAba.add(aba); else soVerNaAba.delete(aba); }
export function acessoNo(g: MeuGrupo | undefined): Acesso {
  if (!g || g.admin) return TUDO;
  const p0 = (g.papeis ?? {})[g.papel] ?? { segs: [], ver: ['*'], editar: [] };
  const p = soVerNaAba.has(g.aba) ? { ...p0, editar: [] } : p0;
  const ver: string[] = p.ver ?? ['*'], editar: string[] = p.editar ?? [];
  return {
    admin: false, nome: p.nome ?? '',
    segs: new Set<Seg>(['base', ...((p.segs ?? []) as Seg[])]),
    ver: col => ver.includes('*') || ver.includes(col),
    editar: col => (ver.includes('*') || ver.includes(col)) && (editar.includes('*') || editar.includes(col)),
  };
}
// ─── Texto 27 (B4.6): "Ver como o cliente": quem administra vê a pasta com o papel do convidado, só olhando ───
let simulacao: { grupo: string; papel: string } | null = null;
let versaoSim = 0;
const ouvSim = new Set<() => void>();
export function setSimulacao(s: { grupo: string; papel: string } | null) { simulacao = s; versaoSim++; ouvSim.forEach(f => f()); }
export const simulandoGrupo = () => simulacao?.grupo ?? null;
export function useSimulacao() {
  useSyncExternalStore(cb => { ouvSim.add(cb); return () => { ouvSim.delete(cb); }; }, () => versaoSim);
  return simulacao;
}
/** Acesso a esta ficha (ficha só minha: tudo). Enquanto a lista de grupos carrega, não bloqueia. */
export function acessoDaFicha(r: Reg | null | undefined): Acesso {
  if (!r?.g) return TUDO;
  const cache = gruposEmCache();
  if (!cache.lista) return TUDO;
  const g = cache.lista.find(x => x.id === r.g);
  if (!g) return { ...TUDO, admin: false, editar: () => false };
  if (simulacao && simulacao.grupo === g.id) return { ...acessoNo({ ...g, admin: false, papel: simulacao.papel }), editar: () => false };
  return acessoNo(g);
}
/** Grupo da aba inteira (sem ficha de origem, ex.: a equipe do consultório) em que os dados estão ligados. */
export const grupoDaAba = (mod: ModId) => gruposEmCache().lista?.find(g => g.aba === mod && !g.ref && g.compartilhar);
/** Pode criar uma ficha desta coleção aqui (dentro de "pai")? */
export function podeCriar(mod: ModId, col: string, pai: Reg | null): boolean {
  if (pai?.g) return acessoDaFicha(pai).editar(col);
  if (!pai) { const g = grupoDaAba(mod); if (g && !g.admin) return acessoNo(g).editar(col); }
  return true;
}
/** Grupo que uma ficha nova deve receber (dentro de uma ficha compartilhada, ou na aba compartilhada inteira). */
export function grupoParaNova(mod: ModId, pai: Reg | null | undefined): string | null {
  if (pai?.g) return pai.g;
  if (!pai) { const g = grupoDaAba(mod); if (g) return g.id; }
  return null;
}

// ─── Partes da ficha ───
function ordenado(v: any): any {
  if (Array.isArray(v)) return v.map(ordenado);
  if (v && typeof v === 'object') return Object.fromEntries(Object.keys(v).sort().map(k => [k, ordenado(v[k])]));
  return v;
}
const assinatura = (v: unknown) => JSON.stringify(ordenado(v));

function anexoParaGrupo(a: Anexo): Anexo | null {
  if (a.cofre) return null;   // criptografado com a chave do cofre de quem guardou: não serve para os outros
  const { nome, mime, tamanho, url, nuvem, gp, uri, aparelho } = a;
  const dado = a.dado && a.dado.length < 280_000 ? a.dado : undefined;
  return Object.fromEntries(Object.entries({ nome, mime, tamanho, url, nuvem, gp, uri, aparelho, dado }).filter(([, v]) => v !== undefined)) as unknown as Anexo;
}

/** Divide a ficha nas partes do grupo. Campos do cofre vão abertos (só se o cofre estiver aberto) para a parte clínica. */
export function partes(mod: ModId, r: Reg): Record<Seg, any> {
  const col = colDe(mod, r.col);
  const c: Record<Seg, Record<string, any>> = { base: {}, fin: {}, clin: {} };
  for (const [k, v] of Object.entries(r.c ?? {})) {
    if (k.startsWith('_')) continue;   // dados só deste aparelho (ex.: lembretes criados no Planner)
    let val = v;
    if (cifrado(val)) { const x = cofreAberto() ? decifrar(val) : null; if (x === null) continue; val = x; }
    c[segDaChave(col, k)][k] = val;
  }
  return {
    base: { c: c.base, criado: r.criado, itens: r.itens ?? [], anexos: (r.anexos ?? []).map(anexoParaGrupo).filter(Boolean) },
    fin: { c: c.fin },
    clin: { c: c.clin },
  };
}

// ─── Estado guardado no aparelho ───
type Estado = { ultimo: Record<string, string>; cheio: Record<string, number>; assin: Record<string, string>;
  auto?: Record<string, 1>;          // grupos que o app já ligou (ou que o administrador desligou de propósito) (0.29)
  vinc?: Record<string, 1>;          // grupos sem ficha de origem que o app já tentou ligar a uma ficha (0.31)
  enviado: Record<string, string>;   // "grupo|ficha|parte" -> assinatura do que está no servidor
  colDe: Record<string, string> };   // "grupo|ficha" -> coleção (para apagar)
let est: Estado = { ultimo: {}, cheio: {}, assin: {}, enviado: {}, colDe: {}, auto: {} };
// servidor sem a migração 0.29 (sem a coluna "ocultar"): envia sem ela
let semOcultar = false;
// a assinatura da parte base inclui quem não vê a ficha, para reenviar quando isso mudar
const assinParte = (r: Reg, s: Seg, p: any) => assinatura(s === 'base' ? [p, r.oc ?? []] : p);
let chave = '';
async function carregarEstado() {
  const eu = await meuId();
  if (!eu) return false;
  if (chave === `compart:${eu}`) return true;
  chave = `compart:${eu}`;
  try { const s = await AsyncStorage.getItem(chave); if (s) est = { ...est, ...JSON.parse(s) }; } catch { /* começa do zero */ }
  return true;
}
let salvarT: ReturnType<typeof setTimeout> | null = null;
const salvarEstado = () => { if (salvarT) clearTimeout(salvarT); salvarT = setTimeout(() => { AsyncStorage.setItem(chave, JSON.stringify(est)).catch(() => {}); }, 800); };
const k3 = (g: string, r: string, s: Seg) => `${g}|${r}|${s}`;
function esquecerFicha(g: string, r: string) { for (const s of SEGS) delete est.enviado[k3(g, r, s)]; delete est.colDe[`${g}|${r}`]; }

// ─── Enviar ───
const rodando = { v: false };
async function enviarArquivos(mod: ModId, r: Reg, g: string): Promise<boolean> {
  const faltam = (r.anexos ?? []).filter(a => !a.cofre && !a.gp && !a.url && temLocal(a));
  if (!faltam.length) return false;
  const feitos = new Map<Anexo, string>();
  for (const a of faltam) { try { feitos.set(a, await enviarArquivoAoGrupo(a, g, r.id)); } catch { /* tenta de novo no próximo ciclo */ } }
  if (!feitos.size) return false;
  setDoc(mod, d => ({ regs: (d?.regs ?? []).map(x => (x.id === r.id ? { ...x, anexos: (x.anexos ?? []).map(a => { const p = [...feitos.entries()].find(([o]) => o.nome === a.nome && o.uri === a.uri && o.dado === a.dado); return p ? { ...a, gp: p[1] } : a; }) } : x)) }));
  return true;
}

async function enviar() {
  if (!supabase) return;
  const cache = gruposEmCache();
  if (!cache.lista || cache.erro) return;
  const linhas: any[] = [];
  const marcar: [string, string][] = [];
  const presentes = new Set<string>();
  for (const mod of MOD_IDS) {
    const regs = getDoc(mod)?.regs ?? [];
    for (const r of regs) {
      if (!r.g) continue;
      const g = cache.lista.find(x => x.id === r.g);
      if (!g?.compartilhar) continue;
      presentes.add(`${g.id}|${r.id}`);
      const ac = acessoNo(g);
      if (!ac.editar(r.col)) continue;
      if (await enviarArquivos(mod, r, g.id)) return;   // a ficha mudou (ganhou o endereço do arquivo): envia no próximo ciclo
      const p = partes(mod, r);
      for (const s of SEGS) {
        if (!ac.segs.has(s)) continue;
        const k = k3(g.id, r.id, s);
        if (s !== 'base' && !Object.keys(p[s].c).length && !est.enviado[k]) continue;
        const a = assinParte(r, s, p[s]);
        if (est.enviado[k] === a) continue;
        linhas.push({ grupo_id: g.id, reg_id: r.id, seg: s, col: r.col, pai: r.pai ?? null, dados: p[s], apagado: false, ...(semOcultar ? {} : { ocultar: r.oc ?? [] }) });
        marcar.push([k, a]);
      }
      est.colDe[`${g.id}|${r.id}`] = r.col;
    }
  }
  // apagadas aqui: avisa o grupo
  for (const [gr, col] of Object.entries(est.colDe)) {
    if (presentes.has(gr)) continue;
    const [gid, rid] = gr.split('|');
    const g = cache.lista.find(x => x.id === gid);
    if (!g?.compartilhar || !acessoNo(g).editar(col)) { esquecerFicha(gid, rid); continue; }
    linhas.push({ grupo_id: gid, reg_id: rid, seg: 'base', col, pai: null, dados: {}, apagado: true });
    marcar.push([`apagar|${gr}`, '']);
  }
  for (let i = 0; i < linhas.length; i += 200) {
    const lote = linhas.slice(i, i + 200), mk = marcar.slice(i, i + 200);
    let { error } = await supabase.from('grupo_dados').upsert(lote, { onConflict: 'grupo_id,reg_id,seg' });
    if (error && !semOcultar && /ocultar/i.test(error.message ?? '')) {
      semOcultar = true;
      lote.forEach(l => { delete l.ocultar; });
      ({ error } = await supabase.from('grupo_dados').upsert(lote, { onConflict: 'grupo_id,reg_id,seg' }));
    }
    if (error) {
      // sem permissão para alguma linha (o papel mudou): envia uma por uma e ignora as recusadas
      for (let j = 0; j < lote.length; j++) {
        const { error: e2 } = await supabase.from('grupo_dados').upsert(lote[j], { onConflict: 'grupo_id,reg_id,seg' });
        if (!e2) aplicarMarca(mk[j]);
      }
    } else mk.forEach(aplicarMarca);
  }
  if (linhas.length) salvarEstado();
}
function aplicarMarca([k, a]: [string, string]) {
  if (k.startsWith('apagar|')) { const [, gid, rid] = k.split('|'); esquecerFicha(gid, rid); }
  else est.enviado[k] = a;
}

// ─── Receber ───
const sigGrupo = (g: MeuGrupo) => `${g.admin}|${g.papel}|${g.compartilhar}|${assinatura(g.papeis ?? {})}`;

async function receberGrupo(g: MeuGrupo, mod: ModId, eu: string) {
  const cheio = !est.ultimo[g.id] || est.assin[g.id] !== sigGrupo(g) || Date.now() - (est.cheio[g.id] ?? 0) > 5 * 60 * 1000;
  // 0.77: o servidor devolve no máximo 1.000 linhas por vez; busca em páginas (antes, acima disso fichas sumiam para os membros).
  const linhas: any[] = [];
  for (let de = 0; de < 50000; de += 1000) {
    let q = supabase!.from('grupo_dados').select('*').eq('grupo_id', g.id).order('atualizado_em', { ascending: true }).order('reg_id').order('seg').range(de, de + 999);
    if (!cheio) q = q.gt('atualizado_em', est.ultimo[g.id]);
    const { data, error } = await q;
    if (error) return;
    linhas.push(...(data ?? []));
    if ((data ?? []).length < 1000) break;
  }
  const souDono = g.dono === eu;
  if (!linhas.length && !cheio) return;
  const jaTinha = new Set((getDoc(mod)?.regs ?? []).filter(r => r.g === g.id).map(r => r.id));
  const porFicha = new Map<string, any[]>();
  for (const l of linhas) { const a = porFicha.get(l.reg_id) ?? []; a.push(l); porFicha.set(l.reg_id, a); }
  setDoc(mod, d => {
    const regs = [...(d?.regs ?? [])];
    const idx = new Map(regs.map((r, i) => [r.id, i]));
    const fora = new Set<string>();
    for (const [rid, ls] of porFicha) {
      const base = ls.find(x => x.seg === 'base');
      const i = idx.get(rid);
      const local = i != null ? regs[i] : null;
      if (local && local.g && local.g !== g.id) continue;   // ficha de outro grupo
      if (base?.apagado) { if (local) fora.add(rid); esquecerFicha(g.id, rid); continue; }
      if (!local && !base) continue;
      let r: Reg = local ? { ...local, c: { ...local.c } } : { id: rid, col: base.col, pai: base.pai ?? null, c: {}, criado: base.dados?.criado ?? base.atualizado_em, alt: base.atualizado_em };
      const col = colDe(mod, base?.col ?? r.col);
      for (const l of ls) {
        const s = l.seg as Seg;
        for (const k of Object.keys(r.c)) if (!k.startsWith('_') && segDaChave(col, k) === s) delete r.c[k];
        Object.assign(r.c, l.dados?.c ?? {});
        if (s === 'base') { r.col = l.col; r.pai = l.pai ?? null; r.itens = l.dados?.itens ?? []; r.anexos = (Array.isArray(l.dados?.anexos) ? l.dados.anexos : []).map(anexoSeguro).filter(Boolean) as Anexo[]; if (l.dados?.criado) r.criado = l.dados.criado; }
      }
      const ult = ls.reduce((a, b) => (a.atualizado_em > b.atualizado_em ? a : b));
      r = { ...r, g: g.id, por: (base ?? ult).atualizado_nome || r.por, alt: ult.atualizado_em };
      if (base && 'ocultar' in base) r.oc = (base.ocultar ?? []).length ? base.ocultar : undefined;
      if (base?.criado_por) r.de = base.criado_por;
      const p = partes(mod, r);
      for (const l of ls) est.enviado[k3(g.id, rid, l.seg)] = assinParte(r, l.seg as Seg, p[l.seg as Seg]);
      est.colDe[`${g.id}|${rid}`] = r.col;
      if (i != null) regs[i] = r; else { idx.set(rid, regs.length); regs.push(r); }
    }
    // leitura completa: o que não veio do servidor saiu do meu alcance (apagado ou o papel mudou)
    if (cheio && !souDono) {
      const vieram = new Set(linhas.filter(l => !l.apagado).map(l => l.reg_id));
      for (const r of regs) if (r.g === g.id && !vieram.has(r.id)) { fora.add(r.id); esquecerFicha(g.id, r.id); }
    }
    return { regs: fora.size ? regs.filter(r => !fora.has(r.id)) : regs };
  });
  // aviso de quem incluiu algo (só neste aparelho e só se o servidor não avisa este celular)
  if (est.ultimo[g.id] && g.avisos !== false && !temPush()) {
    const novos = linhas.filter(l => l.seg === 'base' && !l.apagado && l.atualizado_por !== eu && l.criado_por && l.criado_por !== eu
      && l.atualizado_em > est.ultimo[g.id] && !jaTinha.has(l.reg_id));
    if (novos.length) {
      const quem = novos[0].atualizado_nome || t('Alguém');
      const c = novos[0].dados?.c ?? {};
      const titulo = String(c.nome || c.titulo || c.oque || c.desc || c.item || c.descricao || c.local || '').slice(0, 60);
      avisarAgora('👥 ' + g.nome, novos.length > 1 ? t('{quem} e outros incluíram {n} itens no grupo.', { quem, n: novos.length })
        : t('{quem} incluiu {item} no grupo.', { quem, item: titulo || t('algo novo') }), { tipo: 'grupo', aba: g.aba, grupo: g.id });
    }
  }
  if (linhas.length) est.ultimo[g.id] = linhas[linhas.length - 1].atualizado_em;
  if (cheio) { est.cheio[g.id] = Date.now(); est.assin[g.id] = sigGrupo(g); }
  salvarEstado();
}

/** Saiu do grupo, foi removido, ou o compartilhamento foi desligado: a cópia some (quem criou fica com tudo). */
function limparSemAcesso(eu: string) {
  const cache = gruposEmCache();
  if (!cache.lista || cache.erro) return;
  for (const mod of MOD_IDS) {
    const regs = getDoc(mod)?.regs ?? [];
    if (!regs.some(r => r.g)) continue;
    const sair = new Set<string>(), soltar = new Set<string>();
    for (const r of regs) {
      if (!r.g) continue;
      const g = cache.lista.find(x => x.id === r.g);
      if (g?.compartilhar) continue;
      if (g && (g.dono === eu || g.admin)) soltar.add(r.id); else if (!g || g.dono !== eu) sair.add(r.id);
    }
    if (sair.size || soltar.size) setDoc(mod, d => ({ regs: (d?.regs ?? []).filter(r => !sair.has(r.id)).map(r => (soltar.has(r.id) ? { ...r, g: null } : r)) }));
  }
}

let ultimaLista = 0;
export async function ciclo() {
  if (DEMO || !supabase || rodando.v) return;
  rodando.v = true;
  try {
    if (!(await carregarEstado())) return;
    if (Date.now() - ultimaLista > 2 * 60 * 1000 || !gruposEmCache().lista) { ultimaLista = Date.now(); await recarregarGrupos(); }
    const cache = gruposEmCache();
    if (!cache.lista || cache.erro) return;
    const eu = await meuId();
    await ligarSozinho(eu);
    limparSemAcesso(eu);
    await enviar();
    for (const g of cache.lista) if (g.compartilhar && ehMod(g.aba)) await receberGrupo(g, g.aba, eu);
    await cicloEstudosGrupo();   // 0.71 (texto 09 J): fichário do grupo de estudos (só leitura para os colegas)
  } catch { /* sem internet ou servidor sem a 0.25: tenta de novo depois */ }
  finally { rodando.v = false; }
}

/** 0.29: grupo criado a partir de uma ficha (a viagem, o evento...) já é para compartilhar. Grupos antigos que ficaram
 *  sem os dados ligados são ligados aqui, no aparelho do administrador que tem a ficha, uma vez só (se depois ele
 *  desligar, fica desligado). Nas abas colaborativas, quem entrou como "membro" passa a participar de tudo. */
async function ligarSozinho(eu: string) {
  const cache = gruposEmCache();
  if (!cache.lista) return;
  est.auto ??= {};
  let ligou = false;
  for (const g of cache.lista) {
    if (!g.admin || !ehMod(g.aba) || g.compartilhar === undefined) continue;   // undefined: servidor sem a 0.25
    // 0.31: grupo criado pela tela de grupos (sem ficha de origem): liga à ficha com o mesmo nome (ou à única que houver).
    // Marca própria ("vinc"), porque a 0.29 já tinha marcado esses grupos como vistos.
    if (!g.compartilhar && !g.ref && ehColaborativa(g.aba) && !est.vinc?.[g.id]) {
      const ficha = fichaDoNome(g.aba, g.nome);
      est.vinc = { ...(est.vinc ?? {}), [g.id]: 1 };
      salvarEstado();
      if (ficha) { try { await vincularFicha(g, ficha); ligou = true; } catch { delete est.vinc[g.id]; } }
      continue;   // sem ficha certa: o administrador escolhe no grupo
    }
    if (est.auto[g.id]) continue;
    if (!g.compartilhar && g.ref) {
      if (!(getDoc(g.aba)?.regs ?? []).some(r => r.id === g.ref)) continue;   // a ficha está em outro aparelho
      try { await ligarCompartilhamento(g, false); est.auto[g.id] = 1; salvarEstado(); ligou = true; } catch { /* tenta de novo */ }
      continue;
    }
    if (g.compartilhar && ehColaborativa(g.aba) && !(g.papeis ?? {}).participante) {
      const novos = papeisDaAba(g.aba);
      try { await alterarGrupo(g.id, { papeis: { ...novos, ...(g.papeis ?? {}), participante: novos.participante, membro: novos.membro } }); } catch { continue; }
      est.auto[g.id] = 1; salvarEstado();
      await recarregarGrupos();
    } else est.auto[g.id] = 1;
  }
  // a lista precisa estar atualizada antes da limpeza (senão a ficha recém-ligada seria solta de novo)
  if (ligou) { await recarregarGrupos(); ultimaLista = Date.now(); }
  void eu;
}

/** Fichas principais da aba (a viagem, o evento...) que ainda não estão em outro grupo. */
export function fichasRaiz(mod: ModId) {
  const regs = getDoc(mod)?.regs ?? [];
  const ctx = ctxDe(mod, regs);
  const raiz = new Set(DEFS[mod].colecoes.filter(c => !c.pai).map(c => c.id));
  return regs.filter(r => !r.pai && raiz.has(r.col) && !r.g).map(r => {
    const col = DEFS[mod].colecoes.find(c => c.id === r.col)!;
    return { r, titulo: col.titulo(r, ctx) || '' };
  });
}
/** A ficha cujo nome bate com o do grupo ("Lisboa" e "Lisboa, Portugal"); sem nenhuma parecida, a única que houver. */
function fichaDoNome(mod: ModId, nome: string): Reg | null {
  const fs = fichasRaiz(mod);
  const n = norm(nome).trim();
  const parecidas = fs.filter(f => { const x = norm(f.titulo).trim(); return !!x && !!n && (x.includes(n) || n.includes(x)); });
  if (parecidas.length === 1) return parecidas[0].r;
  if (!parecidas.length && fs.length === 1) return fs[0].r;
  return null;
}
/** Liga o grupo a uma ficha (a viagem, o evento...) e compartilha tudo o que está dentro dela. */
export async function vincularFicha(g: MeuGrupo, r: Reg, rodarCiclo = false) {
  await alterarGrupo(g.id, { ref: r.id });
  await ligarCompartilhamento({ ...g, ref: r.id }, rodarCiclo);
  est.auto = { ...(est.auto ?? {}), [g.id]: 1 };
  salvarEstado();
}

/** Quem incluiu (ou o administrador) escolhe quem do grupo não vê esta ficha; vale também para o que está dentro dela. */
export function ocultarDe(mod: ModId, r: Reg, pessoas: string[]) {
  const regs = getDoc(mod)?.regs ?? [];
  const alvo = new Set<string>([r.id]);
  const fila = [r.id];
  while (fila.length) { const p = fila.shift()!; for (const x of regs) if (x.pai === p && !alvo.has(x.id) && x.g === r.g) { alvo.add(x.id); fila.push(x.id); } }
  setDoc(mod, d => ({ regs: (d?.regs ?? []).map(x => (alvo.has(x.id) ? { ...x, oc: pessoas.length ? pessoas : undefined } : x)) }));
}

/** Fichas novas dentro de uma ficha compartilhada (ou numa aba compartilhada inteira) já nascem no grupo. */
export function registrarRegras() {
  definirGrupoDaNova((mod, r, regs) => {
    if (r.g !== undefined) return r.g ?? null;
    const pai = r.pai ? regs.find(x => x.id === r.pai) ?? null : null;
    return grupoParaNova(mod, pai);
  });
}

let iniciado = false;
let timerEnvio: ReturnType<typeof setTimeout> | null = null;
/** Liga o vaivém dos dados compartilhados (uma vez, depois do login). */
export function iniciarCompartilhamento() {
  if (iniciado || DEMO || !supabase) return;
  iniciado = true;
  setTimeout(() => ciclo(), 3000);
  setInterval(() => { if (AppState.currentState === 'active') ciclo(); }, 30000);
  AppState.addEventListener('change', s => { if (s === 'active') { ultimaLista = 0; ciclo(); } });
  onDocChange(id => {
    if (!ehMod(id) || !(getDoc(id)?.regs ?? []).some(r => r.g)) return;
    if (timerEnvio) clearTimeout(timerEnvio);
    timerEnvio = setTimeout(() => ciclo(), 2500);
  });
  // aviso na hora quando outro membro muda algo (se o tempo real estiver ligado no servidor)
  try { supabase.channel('grupo_dados').on('postgres_changes' as any, { event: '*', schema: 'public', table: 'grupo_dados' }, () => {
    if (timerEnvio) clearTimeout(timerEnvio);
    timerEnvio = setTimeout(() => ciclo(), 1200);
  }).subscribe(); } catch { /* sem tempo real: fica nos 30 segundos */ }
}

/** Fichas que entram no grupo: a ficha de origem e tudo dentro dela, ou a aba inteira (grupo sem ficha de origem). */
function alvoDoGrupo(mod: ModId, g: MeuGrupo): Set<string> {
  const regs = getDoc(mod)?.regs ?? [];
  if (!g.ref) return new Set(regs.filter(r => !r.g || r.g === g.id).map(r => r.id));
  const out = new Set<string>();
  if (!regs.some(r => r.id === g.ref)) return out;
  const fila = [g.ref];
  while (fila.length) { const p = fila.shift()!; out.add(p); for (const r of regs) if (r.pai === p && !out.has(r.id)) fila.push(r.id); }
  return out;
}

/** O administrador liga: os dados da ficha (ou da aba) passam a ser do grupo, com os papéis da aba. */
export async function ligarCompartilhamento(g: MeuGrupo, rodarCiclo = true) {
  if (!ehMod(g.aba)) throw new Error('Esta aba não tem dados para compartilhar.');
  const mod = g.aba;
  const papeis = g.papeis && Object.keys(g.papeis).length ? g.papeis : papeisDaAba(mod);
  await alterarGrupo(g.id, { compartilhar: true, papeis });
  const alvo = alvoDoGrupo(mod, g);
  setDoc(mod, d => ({ regs: (d?.regs ?? []).map(r => (alvo.has(r.id) ? { ...r, g: g.id } : r)) }));
  registrarDemo({ grupo_id: g.id, reg_id: g.ref ?? '', col: '', acao: 'ligou', nome: '', em: new Date().toISOString() });
  est.auto = { ...(est.auto ?? {}), [g.id]: 1 };
  if (rodarCiclo) {
    await recarregarGrupos();
    ultimaLista = Date.now();
    ciclo();
  }
  return alvo.size;
}
/** Desliga: quem criou fica com tudo; os outros membros deixam de ver (a cópia some do aparelho deles). */
export async function desligarCompartilhamento(g: MeuGrupo) {
  await alterarGrupo(g.id, { compartilhar: false });
  est.auto = { ...(est.auto ?? {}), [g.id]: 1 };   // desligado de propósito: não liga sozinho de novo
  salvarEstado();
  for (const mod of MOD_IDS) {
    if (!(getDoc(mod)?.regs ?? []).some(r => r.g === g.id)) continue;
    setDoc(mod, d => ({ regs: (d?.regs ?? []).map(r => (r.g === g.id ? { ...r, g: null } : r)) }));
  }
  await recarregarGrupos();
}
export const papeisDoGrupo = (g: MeuGrupo) => (g.papeis && Object.keys(g.papeis).length ? g.papeis : ehMod(g.aba) ? papeisDaAba(g.aba) : {});
